import React from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import waveSvg from "../assets/img/wave.svg";
import logoBeta from "../assets/img/logo-beta.png";
import logoIcon from "../assets/img/logo_icon.png";
import * as LucideIcons from "lucide-react";
import "../assets/css/agents.css";
import "../assets/css/mainLayout.css";
import LandingChatView from "../components/agents/LandingChatView";
import WorkspaceSection from "../components/agents/WorkspaceSection";
import SAModalConfirm from "../components/common/SAModalConfirm";
import TenantSwitchModal from "../components/common/TenantSwitchModal";
import { getYear, logoutAndPreserveConsent } from "../settings/shared/commonMethods";
import {
  ACCESS_TOKEN,
  API_RESPONSE,
  APP_VERSION,
  SIGNIN_AUTH_TOKEN,
  TENANT_SWITCH_SESSION_KEY,
} from "../settings/shared/constants";
import { AiOutlineDashboard } from "react-icons/ai";
import { GrCubes } from "react-icons/gr";
import { FaCrown } from "react-icons/fa";
import { IoIosCloudOutline } from "react-icons/io";
import { LuShipWheel, LuBuilding2 } from "react-icons/lu";
import BillingService from "../services/api/billing";
import SATooltip from "../components/common/SATooltip";
import { showNotificationGlobal } from "../components/common/NotificationProvider";
import LoginService from "../services/api/login";
import UserService from "../services/api/user";
import { jwtDecode } from "jwt-decode";
import TrialUsageSummary, { TrialCreditAlert } from "../components/common/TrialUsageSummary";
import { TRIAL_USAGE_UPDATED_EVENT } from "../components/agents/useChatMessages";
import { isPocTenant } from "../utils/premiumAccess";
import moment from "moment";

/* ─────────────────────────────────────────────
  Constants
───────────────────────────────────────────── */
const SIDEBAR_EXPANDED = 232;
const SIDEBAR_COLLAPSED = 70;

const PRO_ADVANTAGES = [
  "Higher credit & usage limits",
  "Xray, Audit history, LLM Gateway, Cloud & Cluster",
  "Priority support & advanced integrations",
];

/* ─────────────────────────────────────────────
  Sidebar CSS injected once
───────────────────────────────────────────── */
const SIDEBAR_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .sa-sidebar {
    position: fixed;
    top: 0; left: 0;
    height: 100vh;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    transition: width 0.22s cubic-bezier(0.4,0,0.2,1);
    overflow: hidden;
    z-index: 1100;
    border-right: 0.5px solid #86848414;
  }
  .sa-sidebar.expanded  { width: ${SIDEBAR_EXPANDED}px; }
  .sa-sidebar.collapsed { width: ${SIDEBAR_COLLAPSED}px; }

  /* Logo row */
  .sa-logo-row {
    height: 56px;
    display: flex;
    align-items: center;
    padding: 8px;
    border-bottom: 0.5px solid rgba(0,0,0,0.07);
    flex-shrink: 0;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    overflow: hidden;
  }
  .sa-logo-row img.full-logo  { flex: 1; min-width: 0; max-width: 100%; width: auto; height: auto; object-fit: contain; }
  .sa-logo-row img.mini-logo  { width: 36px; height: 36px; object-fit: contain; flex-shrink: 0; }
  .sa-sidebar.collapsed .sa-logo-row {
    justify-content: center;
    padding: 8px 0;
  }
  .sa-toggle-icon {
    margin-left: auto;
    color: #94a3b8;
    font-size: 14px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s;
  }
  .sa-logo-row:hover .sa-toggle-icon { color: #0f172a; }

  /* Nav area */
  .sa-nav { flex: 1; padding: 10px 0; overflow-x: hidden; overflow-y: auto; }


  .sa-nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    margin: 4px 6px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.14s, color 0.14s;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-decoration: none;
    font-family: 'DM Sans', sans-serif;
  }
  .sa-sidebar.collapsed .sa-nav-item {
    justify-content: center;
    gap: 0;
    padding: 10px 0;
    margin: 4px 8px;
  }
  .sa-nav-item:hover:not(.active) { background: #f8fafc; color: #0f172a; }
  .sa-nav-item.active {
    background: color-mix(in srgb, var(--activemenu, #3d91e4) 11%, #ffffff);
    color: #0f172a;
    font-weight: 600;
  }
  .sa-nav-item.active:hover {
    background: color-mix(in srgb, var(--activemenu, #3d91e4) 16%, #f8fafc);
    color: #0f172a;
  }
  .sa-nav-item.active .sa-nav-icon { color: var(--activemenu, #3d91e4); }

  .sa-nav-icon {
    width: 20px; height: 20px;
    display: flex; align-items: center; justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
    color: inherit;
  }

  .sa-nav-label {
    overflow: hidden;
    transition: max-width 0.22s cubic-bezier(0.4,0,0.2,1), opacity 0.18s;
    max-width: 160px;
    opacity: 1;
  }
  .sa-sidebar.collapsed .sa-nav-label {
    display: none;
    max-width: 0;
    opacity: 0;
  }
  .sa-nav-pro {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    height: 16px;
    padding: 0 6px;
    border-radius: 999px;
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b45309;
    background: linear-gradient(135deg, rgba(245,158,11,0.16) 0%, rgba(217,119,6,0.12) 100%);
    border: 1px solid rgba(217,119,6,0.28);
    line-height: 1;
  }
  .sa-sidebar.collapsed .sa-nav-pro {
    display: none;
  }

    /* Cluster group (super admin) */
  .sa-nav-chev {
    margin-left: auto;
    display: inline-flex;
    color: #94a3b8;
    transition: transform 0.2s ease;
  }
  .sa-nav-chev.is-open { transform: rotate(180deg); }
  .sa-sidebar.collapsed .sa-nav-chev { display: none; }
  .sa-nav-branch.is-open {
    background: color-mix(in srgb, var(--activemenu, #3d91e4) 5%, #ffffff);
    border: 1px solid color-mix(in srgb, var(--activemenu, #3d91e4) 14%, transparent);
    border-radius: 12px;
    margin: 4px 6px;
    padding: 2px 0 4px;
  }
  .sa-nav-branch.is-open > .sa-nav-item { margin: 2px 4px; background: transparent; }
  .sa-nav-sub { display: flex; flex-direction: column; gap: 2px; padding: 0 4px; }
  .sa-nav-sub-item {
    display: flex; flex-direction: column; gap: 1px;
    padding: 7px 10px 7px 38px;
    border-radius: 8px; cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    color: #64748b; font-size: 12.5px; font-weight: 500;
    transition: background 0.14s, color 0.14s;
  }
  .sa-nav-sub-item small { font-size: 10.5px; font-weight: 450; color: #94a3b8; }
  .sa-nav-sub-item:hover:not(.active) { background: rgba(255,255,255,0.75); color: #0f172a; }
  .sa-nav-sub-item.active { background: #fff; color: #0f172a; font-weight: 600; box-shadow: 0 1px 2px rgba(15,23,42,0.05); }
  .sa-nav-sub-item.active small { color: var(--activemenu, #3d91e4); }

  /* Bottom section — match nav-item geometry for alignment */
  .sa-sidebar-bottom {
    padding: 8px 0 10px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  /* Go Pro + Credits stack — visually distinct from nav / profile */
  .sa-sidebar-plan {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 2px 8px 8px;
    padding: 6px;
    border-radius: 12px;
    background: #f8fafc;
    box-sizing: border-box;
  }
  .sa-sidebar.collapsed .sa-sidebar-plan {
    margin: 2px 8px 8px;
    padding: 4px 0;
    gap: 14px;
    align-items: center;
  }

  .sa-credit-trigger {
    --sa-credit-tone: var(--activemenu, #3d91e4);
    display: flex;
    align-items: stretch;
    gap: 0;
    width: 100%;
    box-sizing: border-box;
    border: 0.5px solid color-mix(in srgb, var(--sa-credit-tone) 28%, #e2e8f0);
    background: #fff;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    color: #64748b;
    border-radius: 10px;
    transition: background 0.14s, color 0.14s, border-color 0.14s, box-shadow 0.14s;
    padding: 9px 10px 8px;
    margin: 0;
    text-align: left;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  }
  .sa-credit-trigger.is-ok { --sa-credit-tone: var(--activemenu, #3d91e4); }
  .sa-credit-trigger.is-warn { --sa-credit-tone: #d97706; }
  .sa-credit-trigger.is-critical { --sa-credit-tone: #dc2626; }
  .sa-credit-trigger:hover,
  .sa-credit-trigger.is-open {
    background: color-mix(in srgb, var(--sa-credit-tone) 7%, #ffffff);
    border-color: color-mix(in srgb, var(--sa-credit-tone) 42%, #e2e8f0);
  }
  .sa-credit-trigger-ring {
    display: none;
    position: relative;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .sa-sidebar.collapsed .sa-credit-trigger {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: auto;
    min-height: 44px;
    margin: 0;
    padding: 2px 2px 4px;
    gap: 3px;
    border: none;
    border-radius: 8px;
    background: transparent;
    box-shadow: none;
  }
  .sa-sidebar.collapsed .sa-credit-trigger:hover,
  .sa-sidebar.collapsed .sa-credit-trigger.is-open {
    background: #f8fafc;
    border: none;
    box-shadow: none;
  }
  .sa-sidebar.collapsed .sa-credit-trigger-ring {
    display: inline-flex !important;
  }
  .sa-credit-trigger-rail-label {
    display: none;
  }
  .sa-sidebar.collapsed .sa-credit-trigger-rail-label {
    display: block;
    width: 100%;
    font-size: 8.5px;
    font-weight: 700;
    color: #64748b;
    text-align: center;
    line-height: 1.15;
    letter-spacing: -0.02em;
    white-space: normal;
  }
  .sa-sidebar.collapsed .sa-credit-trigger:hover .sa-credit-trigger-rail-label,
  .sa-sidebar.collapsed .sa-credit-trigger.is-open .sa-credit-trigger-rail-label {
    color: var(--sa-credit-tone);
  }
  .sa-credit-trigger-copy {
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .sa-credit-trigger-top {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }
  .sa-credit-trigger-pct {
    font-size: 18px;
    font-weight: 800;
    color: var(--sa-credit-tone);
    letter-spacing: -0.04em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    font-family: 'DM Sans', sans-serif;
    flex-shrink: 0;
  }
  .sa-credit-trigger-label {
    font-size: 11px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: 0.01em;
    line-height: 1.2;
  }
  .sa-credit-trigger-bar {
    display: block;
    height: 6px;
    border-radius: 99px;
    background: #e2e8f0;
    overflow: hidden;
  }
  .sa-credit-trigger-bar-fill {
    display: block;
    height: 100%;
    border-radius: 99px;
    background: var(--sa-credit-tone);
    transition: width 0.4s ease, background 0.3s ease;
  }
  .sa-credit-trigger-meta {
    font-size: 10.5px;
    font-weight: 500;
    color: #64748b;
    line-height: 1.15;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sa-credit-trigger-chev {
    margin-left: auto;
    color: #94a3b8;
    display: flex;
    flex-shrink: 0;
    transition: transform 0.18s ease, color 0.14s;
  }
  .sa-credit-trigger.is-open .sa-credit-trigger-chev {
    transform: rotate(180deg);
    color: var(--sa-credit-tone);
  }
  .sa-sidebar.collapsed .sa-credit-trigger-copy,
  .sa-sidebar.collapsed .sa-credit-trigger-chev {
    display: none;
  }

  .sa-pro-hint {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
    box-sizing: border-box;
    border: 0.5px solid rgba(217, 119, 6, 0.28);
    background: linear-gradient(180deg, #fffbeb 0%, #fff 100%);
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    color: #92400e;
    border-radius: 10px;
    transition: background 0.14s, color 0.14s, border-color 0.14s, box-shadow 0.14s;
    padding: 8px 10px;
    margin: 0;
    text-align: center;
    box-shadow: 0 1px 2px rgba(180, 83, 9, 0.05);
  }
  .sa-pro-hint:hover,
  .sa-pro-hint.is-open {
    background: linear-gradient(180deg, #fef3c7 0%, #fffbeb 100%);
    border-color: rgba(217, 119, 6, 0.45);
    color: #b45309;
  }
  .sa-sidebar.collapsed .sa-pro-hint {
    padding: 6px 4px;
    gap: 2px;
  }
  .sa-pro-hint-icon {
    width: 22px;
    height: 22px;
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #d97706;
    background: rgba(251, 191, 36, 0.22);
  }
  .sa-pro-hint-icon svg {
    width: 14px;
    height: 14px;
  }
  .sa-sidebar.collapsed .sa-pro-hint-icon {
    width: 18px;
    height: 18px;
    border-radius: 0;
    background: transparent;
  }
  .sa-sidebar.collapsed .sa-pro-hint-icon svg {
    width: 15px;
    height: 15px;
  }
  .sa-pro-hint-label {
    font-size: 11px;
    font-weight: 700;
    color: #92400e;
    letter-spacing: -0.02em;
    line-height: 1.1;
    text-align: center;
  }
  .sa-pro-hint:hover .sa-pro-hint-label,
  .sa-pro-hint.is-open .sa-pro-hint-label {
    color: #b45309;
  }
  .sa-sidebar.collapsed .sa-pro-hint-label {
    font-size: 9px;
  }

  .sa-pro-popup-body {
    padding: 16px 16px 14px;
    font-family: 'DM Sans', sans-serif;
  }
  .sa-pro-popup-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }
  .sa-pro-popup-badge {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(145deg, #fbbf24 0%, #d97706 100%);
    color: #fff;
    flex-shrink: 0;
  }
  .sa-pro-popup-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }
  .sa-pro-popup-sub {
    font-size: 11.5px;
    color: #64748b;
    margin-top: 2px;
  }
  .sa-pro-popup-list {
    list-style: none;
    margin: 0 0 14px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .sa-pro-popup-list li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12.5px;
    color: #334155;
    line-height: 1.35;
    font-weight: 500;
  }
  .sa-pro-popup-check {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    background: rgba(217,119,6,0.14);
    color: #d97706;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .sa-pro-popup-cta {
    width: 100%;
    height: 38px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: #fff;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 650;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
  }
  .sa-pro-popup-cta:hover {
    filter: brightness(1.04);
  }

  .sa-user-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    margin: 4px 6px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.14s;
    overflow: hidden;
    box-sizing: border-box;
    width: auto;
  }
  .sa-user-row:hover { background: #f8fafc; }
  .sa-sidebar.collapsed .sa-user-row {
    justify-content: center;
    align-items: center;
    gap: 0;
    width: auto;
    max-width: 100%;
    margin: 4px 8px;
    padding: 8px 0;
    overflow: visible;
  }

  .sa-user-avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--activemenu, #3d91e4);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-family: 'DM Sans', sans-serif;
  }
  .sa-sidebar.collapsed .sa-user-avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    font-size: 11px;
    margin: 0 auto;
  }

  .sa-user-info {
    overflow: hidden;
    transition: max-width 0.22s, opacity 0.18s;
    max-width: 140px;
    opacity: 1;
    min-width: 0;
  }
  .sa-sidebar.collapsed .sa-user-info {
    display: none !important;
    max-width: 0 !important;
    opacity: 0 !important;
    pointer-events: none;
  }

  .sa-user-name    { font-size: 12.5px; font-weight: 600; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-family: 'DM Sans', sans-serif; }
  .sa-user-account { font-size: 10.5px; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-family: 'DM Sans', sans-serif; }

  .sa-logout-icon {
    margin-left: auto;
    color: #ef4444;
    font-size: 12px;
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 0.18s;
  }
  .sa-sidebar.collapsed .sa-logout-icon {
    display: none !important;
    width: 0 !important;
    margin: 0 !important;
    opacity: 0 !important;
  }
  .sa-sidebar.expanded .sa-user-row:hover .sa-logout-icon { opacity: 1; }

  /* Main wrapper */
  .sa-main-wrapper { transition: margin-left 0.22s cubic-bezier(0.4,0,0.2,1); }

  /* Divider */
  .sa-divider { height: 0.5px; background: rgba(0,0,0,0.07); margin: 4px 10px; }

  /* Profile popup */
  .sa-popup {
    position: fixed;
    bottom: 68px;
    background: #fff;
    border-radius: 16px;
    border: 0.5px solid rgba(0,0,0,0.08);
    box-shadow: 0 16px 40px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.04);
    width: 320px;
    z-index: 1500;
    overflow: hidden;
    padding: 10px 0px;
    animation: saPopupIn 0.16s ease;
    font-family: 'DM Sans', sans-serif;
  }
  @keyframes saPopupIn {
    from { opacity: 0; transform: translateY(8px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .sa-popup-header {
    padding: 16px 18px 14px;
    border-bottom: 0.5px solid rgba(0,0,0,0.07);
  }
  .sa-popup-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    min-height: 30px;
  }
  .sa-popup-meta:last-of-type { margin-bottom: 10px; }
  .sa-popup-meta--full {
    display: block;
    margin-bottom: 10px;
    min-height: 0;
  }
  .sa-open-tenant-modal__wrap {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 8px;
  }
  .sa-open-tenant-modal__wrap .sa-open-tenant-modal {
    margin-top: 0;
  }
  .sa-popup-meta-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .sa-popup-meta-icon--tenant {
    background: color-mix(in srgb, var(--activemenu, #3d91e4) 14%, #ffffff);
    color: var(--activemenu, #3d91e4);
  }
  .sa-popup-meta-icon--email {
    background: #f1f5f9;
    color: #64748b;
  }
  .sa-popup-meta-text {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sa-popup-tenant {
    font-size: 13px;
    font-weight: 700;
    color: var(--activemenu, #3d91e4);
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sa-popup-email { font-size: 12px; color: #64748b; font-weight: 400; line-height: 1.2; }
  .sa-popup-tenant-below-email {
    margin-top: 10px;
  }

  .sa-popup-credit-usage {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
    background: #ffffff;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    overflow: hidden;
    transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.2s;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }
  .sa-popup-credit-usage:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.08), 0 0 0 1px rgba(14,165,233,0.2);
    border-color: rgba(14,165,233,0.35);
  }
  .sa-popup-credit-usage:hover .sa-popup-credit-cta { opacity: 1; }
  .sa-popup-credit-accent {
    position: absolute;
    top: 0; left: 0;
    border-radius: 10px 0 0 10px;
    opacity: 0.95;
  }
  .sa-popup-credit-progress-wrap {
    height: 3px;
    background: #f1f5f9;
    border-radius: 0 0 2px 0;
    overflow: hidden;
  }
  .sa-popup-credit-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #0ea5e9, #14b8a6);
    border-radius: 0 2px 2px 0;
    transition: width 0.4s ease;
  }
  .sa-popup-credit-body {
    padding: 12px 14px 10px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .sa-popup-credit-hero {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .sa-popup-credit-hero-icon {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: #f0f9ff;
    border: 1px solid #e0f2fe;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0ea5e9;
    flex-shrink: 0;
  }
  .sa-popup-credit-hero-text {
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex-wrap: wrap;
  }
  .sa-popup-credit-hero-value {
    font-size: 22px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.03em;
    line-height: 1.1;
    font-family: 'DM Sans', sans-serif;
  }
  .sa-popup-credit-hero-unit {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    align-self: flex-end;
    padding-bottom: 2px;
  }
  .sa-popup-credit-monthly {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #f1f5f9;
  }
  .sa-popup-credit-monthly-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #14b8a6;
    flex-shrink: 0;
    box-shadow: 0 0 6px rgba(20,184,166,0.4);
  }
  .sa-popup-credit-monthly-label {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
  }
  .sa-popup-credit-monthly-value {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    margin-left: auto;
    font-family: 'DM Sans', sans-serif;
  }
  .sa-popup-credit-cta {
    width: 100%;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-align: center;
    padding: 6px 12px;
    background: transparent;
    border: none;
    border-top: 1px solid #f1f5f9;
    border-radius: 0;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s;
  }
  .sa-popup-credit-usage:hover .sa-popup-credit-cta {
    color: #0ea5e9;
  }

  .sa-popup-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 18px;
    font-size: 13px;
    color: #0f172a;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.12s;
    border-bottom: 0.5px solid rgba(0,0,0,0.05);
  }
  .sa-popup-item:last-child { border-bottom: none; }
  .sa-popup-item:hover { background: #f8fafc; }
  .sa-popup-item.danger { color: #ef4444; }
  .sa-popup-item.danger:hover { background: #fff5f5; }
  .sa-popup-item svg { flex-shrink: 0; opacity: 0.65; }
  .sa-popup-item.danger svg { opacity: 1; }

  /* Profile popup: opens workspace switcher dialog */
  .sa-open-tenant-modal {
    width: 100%;
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px solid rgba(148, 163, 184, 0.45);
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(2, 6, 23, 0.04);
    transition: transform 0.14s ease, border-color 0.14s ease, box-shadow 0.14s ease;
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 850;
    color: #0f172a;
  }

  .sa-open-tenant-modal:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: color-mix(in srgb, var(--activemenu, #3d91e4) 38%, rgba(148, 163, 184, 0.45));
    box-shadow: 0 10px 30px rgba(59, 130, 246, 0.12);
  }

  .sa-open-tenant-modal:disabled {
    cursor: not-allowed;
    opacity: 0.8;
  }

  .sa-open-tenant-modal__hint {
    margin: 0;
    padding: 0 4px 2px;
    font-size: 11px;
    color: #94a3b8;
    font-weight: 600;
    line-height: 1.4;
    display: block;
    width: 100%;
  }
`;

/* ─────────────────────────────────────────────
  Tiny inline SVG icons — no react-icons needed
  (kept react-icons imports are no-ops)
───────────────────────────────────────────── */
const IcHome = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <path d="M2 7L8 2l6 5v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M6 16v-6h4v6" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

const IcSettings = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <path
      d="M19.4 13a7.8 7.8 0 0 0 .05-1 7.8 7.8 0 0 0-.05-1l2.1-1.6a.5.5 0 0 0 .12-.65l-2-3.5a.5.5 0 0 0-.6-.22l-2.5 1a7.3 7.3 0 0 0-1.7-1l-.4-2.6a.5.5 0 0 0-.5-.42h-4a.5.5 0 0 0-.5.42l-.4 2.6c-.6.2-1.2.6-1.7 1l-2.5-1a.5.5 0 0 0-.6.22l-2 3.5a.5.5 0 0 0 .12.65L4.6 11c-.03.3-.05.7-.05 1s.02.7.05 1l-2.1 1.6a.5.5 0 0 0-.12.65l2 3.5c.14.24.44.34.7.22l2.5-1c.5.4 1.1.8 1.7 1l.4 2.6c.05.24.26.42.5.42h4c.24 0 .45-.18.5-.42l.4-2.6c.6-.2 1.2-.6 1.7-1l2.5 1c.26.12.56.02.7-.22l2-3.5a.5.5 0 0 0-.12-.65L19.4 13z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const IcXray = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="2" y="2" width="12" height="12" rx="3" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="8" cy="8" r="2.4" stroke="currentColor" strokeWidth="1.25" />
    <path d="M8 4.2v7.6M4.2 8h7.6" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
  </svg>
);

const IcAudit = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 2.2h5.1L12.8 5.8V13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3.2a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" />
    <path d="M9 2.3V6h3.5" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" />
    <path d="M5.2 8.5h5.6M5.2 10.7h3.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const IcStorage = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M2 4.5c0-.6.5-1 1-1h10c.6 0 1 .4 1 1v1c0 .6-.4 1-1 1H3c-.5 0-1-.4-1-1v-1Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path
      d="M2 9c0-.6.5-1 1-1h10c.6 0 1 .4 1 1v1c0 .6-.4 1-1 1H3c-.5 0-1-.4-1-1V9Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <circle cx="4.2" cy="5.5" r="0.7" fill="currentColor" />
    <circle cx="4.2" cy="10" r="0.7" fill="currentColor" />
  </svg>
);

// const IcLocalStorage = () => (
//   <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
//     <path
//       d="M2.5 5.2 8 2.8l5.5 2.4v6.2L8 13.8 2.5 11.4V5.2Z"
//       stroke="currentColor"
//       strokeWidth="1.25"
//       strokeLinejoin="round"
//     />
//     <path d="M8 6.2v5.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
//     <path d="M5.2 7.4 8 8.6l2.8-1.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
//   </svg>
// );

const IcBilling = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="4" width="14" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1 7h14" stroke="currentColor" strokeWidth="1.3" />
    <rect x="3" y="9.5" width="3" height="1.5" rx="0.5" fill="currentColor" />
  </svg>
);

const IcCredits = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" className="sa-popup-credit-icon" aria-hidden="true">
    <circle cx="8" cy="10" r="4" stroke="currentColor" strokeWidth="1.35" />
    <circle cx="13" cy="10" r="4" stroke="currentColor" strokeWidth="1.35" />
    <path d="M5.8 11.8a5.2 5.2 0 0 0 4.2 2.2 5.2 5.2 0 0 0 4.2-2.2M5.8 8.2a5.2 5.2 0 0 1 4.2-2.2 5.2 5.2 0 0 1 4.2 2.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

/** Circular remaining-credit ring for collapsed sidebar */
const CreditProgressRing = ({ pct = 0, size = 40, stroke = 3 }) => {
  const value = Math.min(100, Math.max(0, Number(pct) || 0));
  const rounded = Math.round(value);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  const color =
    value <= 15 ? "#dc2626" : value <= 40 ? "#d97706" : "var(--activemenu, #3d91e4)";
  const label = rounded >= 100 ? "100" : String(rounded);
  const fontSize = rounded >= 100 ? 10.5 : rounded >= 10 ? 12 : 13;

  return (
    <span
      className="sa-credit-trigger-ring"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)", display: "block" }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(148,163,184,0.32)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.45s ease, stroke 0.3s ease" }}
        />
      </svg>
      <span
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0,
          fontSize,
          fontWeight: 750,
          letterSpacing: "-0.04em",
          color,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {label}
        <span style={{ fontSize: fontSize * 0.72, fontWeight: 700, letterSpacing: "-0.08em", marginLeft: 0.5 }}>%</span>
      </span>
    </span>
  );
};

const IcToggle = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="2" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.2" />
    <line x1="5" y1="2" x2="5" y2="14" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const IcSignOut = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <polyline points="10,5 13,8 10,11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="13" y1="8" x2="6" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const IcChevRight = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <polyline points="4,2.5 7.5,6 4,9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IcTenant = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M2.5 14.5V5.2L8 2l5.5 3.2v9.3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.5 14.5v-5h5v5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IcMail = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="2" y="4" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.25" />
    <path d="M2.5 5.5L8 9l5.5-3.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LANDING_SCROLL_HINT_CSS = `
  .sa-scroll-hint {
    position: fixed;
    left: 50%;
    bottom: 18px;
    transform: translateX(-50%);
    z-index: 5;
    user-select: none;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 12px;
    background: transparent;
    border: none;
    outline: none;
    transition: opacity 0.18s ease, transform 0.18s ease;
    animation: saScrollHintNudge 1.7s ease-in-out infinite;
  }
  .sa-scroll-hint:hover {
    transform: translateX(-50%) translateY(-1px);
    opacity: 0.95;
  }
  .sa-scroll-hint__label {
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(15, 23, 42, 0.78);
    line-height: 1;
    margin-bottom: 6px;
    text-shadow: 0 1px 0 rgba(255,255,255,0.7);
  }
  .sa-scroll-hint__chevrons {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 0;
  }
  .sa-scroll-hint__chev {
    width: 14px;
    height: 14px;
    border-right: 2px solid rgba(15, 23, 42, 0.55);
    border-bottom: 2px solid rgba(15, 23, 42, 0.55);
    transform: rotate(45deg);
  }
  .sa-scroll-hint__chev:nth-child(2) {
    margin-top: -6px;
    opacity: 0.55;
  }
  @keyframes saScrollHintNudge {
    0%   { opacity: 0.30; transform: translateX(-50%) translateY(-2px); }
    35%  { opacity: 0.95; transform: translateX(-50%) translateY(6px); }
    60%  { opacity: 0.55; transform: translateX(-50%) translateY(2px); }
    100% { opacity: 0.30; transform: translateX(-50%) translateY(-2px); }
  }
  @media (prefers-reduced-motion: reduce) {
    .sa-scroll-hint { animation: none; }
    .sa-scroll-hint { opacity: 0.85; }
  }
`;

const LANDING_WAVES_CSS = `
  .particle-container.sa-landing-hero {
    overflow: hidden;
  }

  .particle-container.sa-landing-hero .sa-landing-wave-svg {
    position: absolute;
    left: -1%;
    right: 0%;
    bottom: -2px;
    height: 320px;
    background-image: var(--sa-wave-svg);
    background-repeat: repeat-x;
    background-position: 0 100%;
    background-size: 1440px 320px;
    opacity: 0.65;
    z-index: 0;
    pointer-events: none;
    background-size: cover;
  }

  @keyframes saWaveSvgDrift {
    from { transform: translate3d(0,0,0); }
    to   { transform: translate3d(260px,0,0); }
  }

  .particle-container.sa-landing-hero .particles { z-index: 1; }
  .particle-container.sa-landing-hero .chat-main-content { position: relative; z-index: 2; }

  @media (prefers-reduced-motion: reduce) {
    .particle-container.sa-landing-hero .sa-landing-wave-svg { animation: none; }
  }
`;

/* ─────────────────────────────────────────────
  Hub sidebar: active item follows the current route
───────────────────────────────────────────── */
const hubActiveNav = (pathname, search) => {
  const p = pathname || "";
  // if (p.startsWith("/storage")) return "localStorage";
  if (p.startsWith("/storage")) return "storage";
  if (p.startsWith("/dashboard")) return "dashboard";
  if (p.startsWith("/audit-history")) return "audit";
  if (p.startsWith("/xray") || p.startsWith("/apps/import")) return "xray";
  if (p.startsWith("/stack")) return "stack";
  if (p.startsWith("/accounts")) return "accounts";
  if (p.startsWith("/clusters/configuration")) return "cluster-config";
  if (p.startsWith("/clusters/resources")) return "cluster-workloads";
  if (p.startsWith("/clusters")) return "clusters";
  if (p.startsWith("/cloud")) return "cloud";
  if (p.startsWith("/settings")) {
    try {
      const q = new URLSearchParams(search || "");
      if (q.get("section") === "credit-usage") return "billing";
    } catch (e) {
      /* ignore */
    }
    return "settings";
  }
  if (p.endsWith("/apps") || p.startsWith("/apps/")) return "home";
  return "home";
};

const navItemClass = (activeNav, key) => `sa-nav-item${activeNav === key ? " active" : ""}`;

const navKeyHandler = (fn) => (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    fn();
  }
};

/* ─────────────────────────────────────────────
  Sidebar component
───────────────────────────────────────────── */
const Sidebar = ({
  expanded,
  onToggle,
  activeNav,
  onHome,
  onXray,
  onStorage,
  // onLocalStorage,
  onDashboard,
  onAudit,
  onSettings,
  onStack,
  onCluster,
  onClusterConfig,
  onClusterResources,
  onCloud,
  onAccounts,
  isPlatformAdmin = false,
  showStack = false,
  onProfileClick,
  onCreditClick,
  onProClick,
  creditOpen = false,
  proOpen = false,
  showProHint = false,
  avatar,
  fullName,
  accountName,
  trialUsage,
}) => {
  const totalCredits = Number(trialUsage?.totalCredits) || 0;
  const remainingCredits = Math.max(0, totalCredits - (Number(trialUsage?.usedCredits) || 0));
  const remainPct = totalCredits > 0 ? Math.min(100, Math.max(0, (remainingCredits / totalCredits) * 100)) : 0;
  const remainPctRounded = Math.round(remainPct);
  const daysLeft = Number(trialUsage?.daysLeft) || 0;
  const expired = Boolean(trialUsage?.trialExpired);
  const creditToneClass = expired || remainPct <= 15 ? "is-critical" : remainPct <= 40 ? "is-warn" : "is-ok";
  const creditMeta = expired
    ? "Trial ended"
    : `${remainingCredits.toLocaleString()} remaining${daysLeft ? ` · ${daysLeft}d left` : ""}`;
  const creditTitle = expired
    ? `${remainingCredits.toLocaleString()} credits · Ended`
    : `${remainPctRounded}% available · ${remainingCredits.toLocaleString()} remaining · ${daysLeft}d left`;

  return (
  <div className={`sa-sidebar ${expanded ? "expanded" : "collapsed"}`}>

    {/* Logo / toggle row */}
    <div className="sa-logo-row" onClick={onToggle} title={expanded ? "Collapse sidebar" : "Expand sidebar"}>
      {expanded ? (
        <>
          <img src={logoBeta} alt="SuperAgentX" className="full-logo" />
          <span className="sa-toggle-icon"><IcToggle /></span>
        </>
      ) : (
        <img src={logoIcon} alt="SuperAgentX" className="mini-logo" />
      )}
    </div>

    {/* Navigation */}
       <nav className="sa-nav">
        {isPlatformAdmin ? (
          <>
            <SATooltip title={!expanded ? "Accounts" : ""} placement="right">
              <div
                role="button"
                tabIndex={0}
                className={navItemClass(activeNav, "accounts")}
                onClick={onAccounts}
                onKeyDown={navKeyHandler(onAccounts)}
              >
                <span className="sa-nav-icon"><LuBuilding2 /></span>
                <span className="sa-nav-label">Accounts</span>
              </div>
            </SATooltip>

            <SATooltip title={!expanded ? "Stack" : ""} placement="right">
              <div
                role="button"
                tabIndex={0}
                className={navItemClass(activeNav, "stack")}
                onClick={onStack}
                onKeyDown={navKeyHandler(onStack)}
              >
                <span className="sa-nav-icon"><GrCubes /></span>
                <span className="sa-nav-label">Stack</span>
              </div>
            </SATooltip>

            <SATooltip title={!expanded ? "Cloud" : ""} placement="right">
              <div
                role="button"
                tabIndex={0}
                className={navItemClass(activeNav, "cloud")}
                onClick={onCloud}
                onKeyDown={navKeyHandler(onCloud)}
              >
                <span className="sa-nav-icon"><IoIosCloudOutline size={18} /></span>
                <span className="sa-nav-label">Cloud</span>
              </div>
            </SATooltip>

            <SATooltip title={!expanded ? "Cluster" : ""} placement="right">
              <div
                role="button"
                tabIndex={0}
                className={navItemClass(activeNav, "clusters")}
                onClick={onCluster}
                onKeyDown={navKeyHandler(onCluster)}
              >
                <span className="sa-nav-icon"><LuShipWheel /></span>
                <span className="sa-nav-label">Cluster</span>
              </div>
            </SATooltip>
          </>
        ) : (
        <>
          <SATooltip title={!expanded ? "Home" : ""} placement="right">
            <div
              role="button"
              tabIndex={0}
              className={navItemClass(activeNav, "home")}
              onClick={onHome}
              onKeyDown={navKeyHandler(onHome)}
            >
              <span className="sa-nav-icon"><IcHome /></span>
              <span className="sa-nav-label">Home</span>
            </div>
          </SATooltip>

          <SATooltip title={!expanded ? (showProHint ? "Xray · Pro" : "Xray") : ""} placement="right">
            <div
              role="button"
              tabIndex={0}
              className={navItemClass(activeNav, "xray")}
              onClick={onXray}
              onKeyDown={navKeyHandler(onXray)}
            >
              <span className="sa-nav-icon"><IcXray /></span>
              <span className="sa-nav-label">Xray</span>
              {showProHint ? <span className="sa-nav-pro">Pro</span> : null}
            </div>
          </SATooltip>

          <SATooltip title={!expanded ? "Storage" : ""} placement="right">
            <div
              role="button"
              tabIndex={0}
              className={navItemClass(activeNav, "storage")}
              onClick={onStorage}
              onKeyDown={navKeyHandler(onStorage)}
            >
              <span className="sa-nav-icon"><IcStorage /></span>
              <span className="sa-nav-label">Storage</span>
            </div>
          </SATooltip>

          <SATooltip title={!expanded ? "Dashboard" : ""} placement="right">
            <div
              role="button"
              tabIndex={0}
              className={navItemClass(activeNav, "dashboard")}
              onClick={onDashboard}
              onKeyDown={navKeyHandler(onDashboard)}
            >
              <span className="sa-nav-icon"><AiOutlineDashboard size={18} /></span>
              <span className="sa-nav-label">Dashboard</span>
            </div>
          </SATooltip>
      <SATooltip title={!expanded ? (showProHint ? "Audit history · Pro" : "Audit history") : ""} placement="right">
        <div
          role="button"
          tabIndex={0}
          className={navItemClass(activeNav, "audit")}
          onClick={onAudit}
          onKeyDown={navKeyHandler(onAudit)}
        >
          <span className="sa-nav-icon"><IcAudit /></span>
          <span className="sa-nav-label">Audit history</span>
          {showProHint ? <span className="sa-nav-pro">Pro</span> : null}
        </div>
      </SATooltip>

      <SATooltip title={!expanded ? "Settings" : ""} placement="right">
        <div
          role="button"
          tabIndex={0}
          className={navItemClass(activeNav, "settings")}
          onClick={onSettings}
          onKeyDown={navKeyHandler(onSettings)}
        >
          <span className="sa-nav-icon"><IcSettings /></span>
          <span className="sa-nav-label">Settings</span>
        </div>
      </SATooltip>
        </>
      )}
    </nav>
    {/* User / profile bottom row */}
    <div className="sa-sidebar-bottom">
      {(showProHint || trialUsage) ? (
        <div className="sa-sidebar-plan">
          {showProHint ? (
              <button
                type="button"
                className={`sa-pro-hint${proOpen ? " is-open" : ""}`}
                onClick={onProClick}
                aria-expanded={proOpen}
                aria-haspopup="dialog"
                aria-label="Upgrade plan"
              >
                <span className="sa-pro-hint-icon">
                  <FaCrown size={16} aria-hidden="true" />
                </span>
                <span className="sa-pro-hint-label">Upgrade</span>
              </button>
          ) : null}
          {trialUsage ? (
            <SATooltip title={!expanded ? creditTitle : ""} placement="right">
              <button
                type="button"
                className={`sa-credit-trigger ${creditToneClass}${creditOpen ? " is-open" : ""}`}
                onClick={onCreditClick}
                aria-expanded={creditOpen}
                aria-haspopup="dialog"
                aria-label={creditTitle}
              >
                <CreditProgressRing pct={remainPct} size={40} />
                <span className="sa-credit-trigger-rail-label">Available credits</span>
                <span className="sa-credit-trigger-copy">
                  <span className="sa-credit-trigger-top">
                    <span className="sa-credit-trigger-pct">{remainPctRounded}%</span>
                    <span className="sa-credit-trigger-chev" aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 7.5L6 4l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </span>
                  <span className="sa-credit-trigger-bar" aria-hidden="true">
                    <span
                      className="sa-credit-trigger-bar-fill"
                      style={{ width: `${remainPct}%` }}
                    />
                  </span>
                  <span className="sa-credit-trigger-label">Available credits</span>
                  <span className="sa-credit-trigger-meta">{creditMeta}</span>
                </span>
              </button>
            </SATooltip>
          ) : null}
        </div>
      ) : null}
      <div className="sa-divider" style={{ margin: "0 0 6px" }} />
      <SATooltip title={!expanded ? fullName : ""} placement="right">
        <div className="sa-user-row" onClick={onProfileClick}>
          <div className="sa-user-avatar">{(avatar || "U").toUpperCase()}</div>
          <div className="sa-user-info">
            <div className="sa-user-name">{fullName}</div>
            <div className="sa-user-account">{accountName}</div>
          </div>
          <span className="sa-logout-icon"><IcSignOut /></span>
        </div>
      </SATooltip>
    </div>
  </div>
  );
};

/* ─────────────────────────────────────────────
  Profile Popup
───────────────────────────────────────────── */
const ProfilePopup = ({
  tenantName,
  email,
  lastLogin,
  billingData,
  sidebarWidth,
  availableTenants = [],
  switchingTenant = false,
  isPlatformAdmin = false,
  onSettings,
  onBilling,
  onSignOut,
  onClose,
  onOpenTenantModal,
}) => {
  const summaryUsage = billingData?.summaryUsage;
  const creditUsage = billingData?.creditUsage;

  const totalCredits =
    summaryUsage?.totalCredits != null
      ? Number(summaryUsage.totalCredits)
      : (creditUsage?.credits?.total != null ? Number(creditUsage.credits.total) : 0);

  const currentMonthCredits =
    summaryUsage?.currentMonthCredits != null
      ? Number(summaryUsage.currentMonthCredits)
      : (creditUsage?.monthlyUsed != null ? Number(creditUsage.monthlyUsed) : 0);

    const menuItems = [
  ...(isPlatformAdmin
    ? []
    : [{
        icon: <IcSettings />,
        label: "Settings",
        onClick: onSettings,
      }]),
 
  ];

  return (
    <div
      className="sa-popup"
      style={{ left: sidebarWidth + 12 }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="sa-popup-header">
        <div className="sa-popup-meta">
          <span className="sa-popup-meta-icon sa-popup-meta-icon--email" aria-hidden="true">
            <IcMail />
          </span>
          <div className="sa-popup-meta-text sa-popup-email" style={{ minWidth: 0 }}>
            {email || "—"}
          </div>
        </div>
        {tenantName ? (
          <div className="sa-popup-meta sa-popup-tenant-below-email">
            <span className="sa-popup-meta-icon sa-popup-meta-icon--tenant" aria-hidden="true">
              <IcTenant />
            </span>
            <div className="sa-popup-meta-text" style={{ minWidth: 0 }}>
              <div className="sa-popup-tenant">{tenantName}</div>
            </div>
          </div>
        ) : null}
        <div
  className="sa-popup-meta sa-popup-last-login"
  style={{
    marginTop: 6,
    paddingTop: 6,
    borderTop: "1px solid #f1f5f9",
  }}
>
  <span
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 20,
      height: 20,
      color: "#94a3b8",
    }}
  >
    <LucideIcons.Clock3 size={13} />
  </span>

  <div
    className="sa-popup-meta-text"
    style={{
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
    }}
  >
    <span
      style={{
        fontSize: 11,
        color: "#94a3b8",
      }}
    >
      Last login
    </span>

    <span
      style={{
        fontSize: 11,
        color: "#475569",
        marginLeft: 8,
      }}
    >
      {lastLogin
        ? moment.utc(lastLogin).local().format("MMM DD, YYYY · hh:mm A")
        : "Not logged in yet"}
    </span>
  </div>
</div>
        {Array.isArray(availableTenants) && availableTenants.length > 1 ? (
          <div className="sa-popup-meta sa-popup-meta--full">
            <div className="sa-open-tenant-modal__wrap">
              <button
                type="button"
                className="sa-open-tenant-modal"
                onClick={onOpenTenantModal}
                disabled={switchingTenant}
                aria-label="Open workspace switcher"
                title="Open workspace switcher"
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <IcTenant />
                  <span>{switchingTenant ? "Switching workspace…" : "Switch workspace"}</span>
                </span>
                <span aria-hidden="true" style={{ display: "flex", alignItems: "center", color: "#94a3b8" }}>
                  <IcChevRight />
                </span>
              </button>
              <p className="sa-open-tenant-modal__hint">
                Search and pick another organization.
              </p>
            </div>
          </div>
        ) : null}
        {!isPlatformAdmin && (
          <div className="sa-popup-credit-usage">
          <div className="sa-popup-credit-accent" aria-hidden="true" />
          <div className="sa-popup-credit-progress-wrap">
            <div
              className="sa-popup-credit-progress-fill"
              style={{
                width: "100%",
                borderLeft: "1px solid currentColor",
              }}
              aria-hidden="true"
            />
          </div>
          <div className="sa-popup-credit-body">
            <div className="sa-popup-credit-hero">
              <span className="sa-popup-credit-hero-icon"><IcCredits /></span>
              <div className="sa-popup-credit-hero-text">
                <span className="sa-popup-credit-hero-value">
                  {typeof totalCredits === "number" ? totalCredits.toFixed(1) : totalCredits}
                </span>
                <span className="sa-popup-credit-hero-unit">total credits</span>
              </div>
            </div>
            <div className="sa-popup-credit-monthly">
              <span className="sa-popup-credit-monthly-dot" aria-hidden="true" />
              <span className="sa-popup-credit-monthly-label">This month</span>
              <span className="sa-popup-credit-monthly-value">
                {typeof currentMonthCredits === "number" ? currentMonthCredits.toFixed(1) : currentMonthCredits}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="sa-popup-credit-cta"
            onClick={onBilling}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onBilling();
              }
            }}
          >
              View usage
            </button>
          </div>
        )}
      </div>

      {/* Menu items */}
      <div>
        {menuItems.map(({ icon, label, onClick }) => (
          <div key={label} className="sa-popup-item" onClick={onClick}>
            {icon}
            <span>{label}</span>
            <span style={{ marginLeft: "auto", color: "#cbd5e1" }}><IcChevRight /></span>
          </div>
        ))}
        <div className="sa-popup-item danger" onClick={onSignOut}>
          <IcSignOut />
          <span>Sign out</span>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
  Shared shell: same sidebar for home, storage, settings
───────────────────────────────────────────── */
export const LandingHubLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarExpanded, setSidebarExpanded] = React.useState(false);
  const [switchingTenant, setSwitchingTenant] = React.useState(false);
  const [state, setState] = React.useState({
    showLogoutFlag: false,
    showProfilePopup: false,
    showCreditPopup: false,
    showProPopup: false,
    showTenantSwitchModal: false,
    creditAlertDismissed: false,
    billingData: {},
    trialUsage: null,
  });

  const activeNav = React.useMemo(
    () => hubActiveNav(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const profile = JSON.parse(localStorage.getItem("profile") || "{}");
  const first = profile.firstName || "";
  const last = profile.lastName || "";
  const fullName = `${first} ${last}`.trim() || "User";
  const avatar = (first?.[0] || "U");
  const accountName = profile?.accountName || "";
  const email = profile?.email || "";
  const currentAccountId = localStorage.getItem("accountId");
  const role = localStorage.getItem("role");
  const isPlatformAdmin = role === "PLATFORM_ADMIN";

  const availableTenants = React.useMemo(() => {
    try {
      const raw = localStorage.getItem("tenantList");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }, []);

  React.useEffect(() => {
    const id = "sa-sidebar-styles-v15";
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement("style");
      tag.id = id;
      document.head.appendChild(tag);
    }
    tag.textContent = SIDEBAR_CSS;
    ["sa-sidebar-styles-v2", "sa-sidebar-styles-v3", "sa-sidebar-styles-v4", "sa-sidebar-styles-v5", "sa-sidebar-styles-v6", "sa-sidebar-styles-v7", "sa-sidebar-styles-v8", "sa-sidebar-styles-v9", "sa-sidebar-styles-v10", "sa-sidebar-styles-v11", "sa-sidebar-styles-v12", "sa-sidebar-styles-v13", "sa-sidebar-styles-v14"].forEach((legacyId) => {
      const legacy = document.getElementById(legacyId);
      if (legacy) legacy.remove();
    });
  }, []);

  React.useEffect(() => {
    if (!state.showProfilePopup && !state.showCreditPopup && !state.showProPopup) return;
    const handler = (e) => {
      if (
        !e.target.closest(".sa-popup") &&
        !e.target.closest(".sa-user-row") &&
        !e.target.closest(".sa-credit-trigger") &&
        !e.target.closest(".sa-pro-hint")
      ) {
        setState((p) => ({
          ...p,
          showProfilePopup: false,
          showCreditPopup: false,
          showProPopup: false,
        }));
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [state.showProfilePopup, state.showCreditPopup, state.showProPopup]);

  React.useEffect(() => {
    if (!state.showProfilePopup) return;
    const accountId = localStorage.getItem("accountId");
    if (!accountId) return;

    let cancelled = false;
    BillingService.getAccountCreditUsageSummary().then((response) => {
      if (cancelled) return;
      if (response?.status === API_RESPONSE.SUCCESS) {
        setState((prevState) => ({
          ...prevState,
          billingData: response,
          trialUsage: response?.trialUsageSummary || prevState.trialUsage,
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [state.showProfilePopup]);

  React.useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    if (!accountId) return;

    let cancelled = false;
    BillingService.getTrialUsageSummary()
      .then((response) => {
        if (cancelled) return;
        const trial = response?.trialUsageSummary;
        if (trial) {
          setState((prev) => ({
            ...prev,
            trialUsage: trial,
            creditAlertDismissed: false,
          }));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    const onTrialUpdated = (event) => {
      const trial = event?.detail;
      if (!trial) return;
      setState((prev) => ({
        ...prev,
        trialUsage: trial,
        creditAlertDismissed: false,
      }));
    };
    window.addEventListener(TRIAL_USAGE_UPDATED_EVENT, onTrialUpdated);
    return () => window.removeEventListener(TRIAL_USAGE_UPDATED_EVENT, onTrialUpdated);
  }, []);

  const token = localStorage.getItem(ACCESS_TOKEN);
  if (token === null) navigate("/auth/login");

  const sidebarWidth = sidebarExpanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;

  const clearLogout = () => logoutAndPreserveConsent();

  const switchTenant = React.useCallback(
    async (tenant) => {
      const nextAccountId = tenant?.accountID ?? tenant?.accountId ?? tenant?.id;
      if (!nextAccountId) return;

      if (currentAccountId && String(currentAccountId) === String(nextAccountId)) return;

      const loginEmail = (localStorage.getItem("loginEmail") || email || "").toLowerCase();
      if (!loginEmail) {
        showNotificationGlobal("Unable to switch tenant: missing email. Please login again.", "error");
        return;
      }

      let willReload = false;
      try {
        setSwitchingTenant(true);

        const verifyRes = await LoginService.switchTenant(tenant?.accountName, loginEmail);
        if (verifyRes?.status !== API_RESPONSE.SUCCESS || !verifyRes?.authToken) {
          showNotificationGlobal(
            verifyRes?.errorMessage || verifyRes?.message || "Failed to switch tenant",
            "error"
          );
          return;
        }

        sessionStorage.setItem(SIGNIN_AUTH_TOKEN, verifyRes.authToken);

        const validateRes = await LoginService.validateAuth({
          email: loginEmail,
          accountId: nextAccountId
        });

        if (validateRes?.status !== API_RESPONSE.SUCCESS || !validateRes?.token) {
          showNotificationGlobal(validateRes?.errorMessage || "Failed to switch tenant", "error");
          return;
        }

        localStorage.setItem("accountLogo", tenant?.accountLogo || "");
        localStorage.setItem("accountName", tenant?.accountName || "");

        localStorage.setItem(ACCESS_TOKEN, validateRes.token);
        const decoded = jwtDecode(validateRes.token);
        if (decoded?.id != null) localStorage.setItem("Uid", decoded.id);
        if (decoded?.roles?.length > 0) {
          localStorage.setItem("role", decoded.roles[0]);
}

        try {
          const res = await UserService.getuserdetails();
          localStorage.setItem("profile", JSON.stringify(res));
          localStorage.setItem("accountId", res.accountId);
          localStorage.setItem("tenant", res.tenant);
        } catch (e) {}

        showNotificationGlobal("Tenant switched successfully.", "success");
        willReload = true;
        try {
          sessionStorage.setItem(TENANT_SWITCH_SESSION_KEY, "1");
        } catch (e) {
          /* ignore */
        }
        window.location.reload();
      } catch (err) {
        showNotificationGlobal(err?.response?.data?.errorMessage || err?.message || "Failed to switch tenant", "error");
      } finally {
        if (!willReload) setSwitchingTenant(false);
      }
    },
    [currentAccountId, email]
  );

  const handleHome = () => {
    navigate("/apps/");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

  const handleXray = () => {
    navigate("/xray");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

  const handleSettings = () => {
    navigate("/settings?view=agentApps");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

    const handleStack = () => {
    navigate("/stack");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false, }));
  };

  const handleCluster = () => {
    navigate("/clusters");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

    const handleClusterConfig = () => {
    navigate("/clusters/configuration");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

  const handleClusterResources = () => {
    navigate("/clusters/resources");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

  const handleAccounts = () => {
    navigate("/accounts");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

  const handleCloud = () => {
    navigate("/cloud");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

  const handleStorage = () => {
    navigate("/storage");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

  // const handleLocalStorage = () => {
  //   navigate("/storage");
  //   setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  // };

  const handleDashboard = () => {
    navigate("/dashboard");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

  const handleAudit = () => {
    navigate("/audit-history");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

  const handleBilling = () => {
    navigate("/settings?view=credit-usage");
    setState((p) => ({ ...p, showProfilePopup: false, showCreditPopup: false, showProPopup: false }));
  };

  const handleOpenTenantModal = () => {
    setState((p) => ({
      ...p,
      showProfilePopup: false,
      showCreditPopup: false,
      showProPopup: false,
      showTenantSwitchModal: true,
    }));
  };

  const handleProfileClick = () => {
    setState((p) => ({
      ...p,
      showProfilePopup: !p.showProfilePopup,
      showCreditPopup: false,
      showProPopup: false,
    }));
  };

  const handleCreditClick = () => {
    const willOpen = !state.showCreditPopup;
    setState((p) => ({
      ...p,
      showCreditPopup: willOpen,
      showProfilePopup: false,
      showProPopup: false,
    }));

    if (!willOpen) return;

    BillingService.getTrialUsageSummary()
      .then((response) => {
        const trial = response?.trialUsageSummary;
        if (!trial) return;
        setState((prev) => ({
          ...prev,
          trialUsage: trial,
        }));
      })
      .catch(() => {});
  };

  const handleProClick = () => {
    setState((p) => ({
      ...p,
      showProPopup: !p.showProPopup,
      showProfilePopup: false,
      showCreditPopup: false,
    }));
  };

  return (
    <div
      style={{
        display: "flex",
        fontFamily: "'DM Sans', sans-serif",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <Sidebar
        expanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded((v) => !v)}
        activeNav={activeNav}
        onHome={handleHome}
        onXray={handleXray}
        onStorage={handleStorage}
        // onLocalStorage={handleLocalStorage}
        onDashboard={handleDashboard}
        onAudit={handleAudit}
        onSettings={handleSettings}
        onStack={handleStack}
        onCluster={handleCluster}
        onClusterConfig={handleClusterConfig}
        onClusterResources={handleClusterResources}
        onCloud={handleCloud}
        onAccounts={handleAccounts}
        isPlatformAdmin={isPlatformAdmin}
        showStack={isPlatformAdmin}
        onBilling={handleBilling}
        onProfileClick={handleProfileClick}
        onProClick={handleProClick}
        creditOpen={state.showCreditPopup}
        proOpen={state.showProPopup}
        // showProHint={isPocTenant()}
        showProHint={isPocTenant()}
        avatar={avatar}
        fullName={fullName}
        accountName={accountName}
        // trialUsage={state.trialUsage}
        trialUsage={isPlatformAdmin ? null : state.trialUsage}
      />

      <div
        style={{
          marginLeft: sidebarWidth,
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {!isPlatformAdmin && !state.creditAlertDismissed ? (
          <TrialCreditAlert
            trial={state.trialUsage}
            onUpgrade={handleBilling}
            onDismiss={() => setState((p) => ({ ...p, creditAlertDismissed: true }))}
          />
        ) : null}
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <Outlet />
        </div>
      </div>

      {state.showProPopup ? (
        <div
          className="sa-popup"
          style={{ left: sidebarWidth + 12, bottom: sidebarExpanded ? 160 : 150 }}
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Pro plan advantages"
        >
          <div className="sa-pro-popup-body">
            <div className="sa-pro-popup-head">
              <span className="sa-pro-popup-badge" aria-hidden="true">
                <FaCrown size={15} />
              </span>
              <div>
                <div className="sa-pro-popup-title">Upgrade to Pro</div>
                <div className="sa-pro-popup-sub">Built for teams that ship faster</div>
              </div>
            </div>
            <ul className="sa-pro-popup-list">
              {PRO_ADVANTAGES.map((item) => (
                <li key={item}>
                  <span className="sa-pro-popup-check" aria-hidden="true">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6.2l2.3 2.3 4.7-4.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <button type="button" className="sa-pro-popup-cta" onClick={handleBilling}>
              <FaCrown size={13} aria-hidden="true" />
              Upgrade to Premium
            </button>
          </div>
        </div>
      ) : null}

      {state.showCreditPopup ? (
        <div
          className="sa-popup"
          style={{ left: sidebarWidth + 12, bottom: sidebarExpanded ? 118 : 108 }}
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Trial credit usage"
        >
          {state.trialUsage ? (
            <TrialUsageSummary
              trial={state.trialUsage}
              embedded
              onViewUsage={handleBilling}
            />
          ) : (
            <div
              style={{
                padding: "28px 20px",
                textAlign: "center",
                color: "#64748b",
                fontSize: 13,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Loading credit usage…
            </div>
          )}
        </div>
      ) : null}

      {state.showProfilePopup && (
        <ProfilePopup
          tenantName={accountName}
          email={email}
          lastLogin={profile?.lastLogin}
          billingData={state.billingData}
          sidebarWidth={sidebarWidth}
          availableTenants={availableTenants}
          switchingTenant={switchingTenant}
          isPlatformAdmin={isPlatformAdmin}
          onSettings={handleSettings}
          onBilling={handleBilling}
          onSignOut={() => setState((p) => ({ ...p, showLogoutFlag: true, showProfilePopup: false }))}
          onClose={() => setState((p) => ({ ...p, showProfilePopup: false }))}
          onOpenTenantModal={handleOpenTenantModal}
        />
      )}

      <TenantSwitchModal
        show={state.showTenantSwitchModal}
        activeTenantName={accountName}
        availableTenants={availableTenants}
        currentAccountId={currentAccountId}
        switchingTenant={switchingTenant}
        onSwitchTenant={switchTenant}
        onClose={() => setState((p) => ({ ...p, showTenantSwitchModal: false }))}
      />

      <SAModalConfirm
        show={state.showLogoutFlag}
        title="Sign out"
        message="Are you sure you want to sign out?"
        okText="Sign out"
        cancelText="Cancel"
        handleOk={clearLogout}
        handleCancel={() => setState((p) => ({ ...p, showLogoutFlag: false }))}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────
  Home / apps page body (rendered inside hub layout)
───────────────────────────────────────────── */
const Landing = () => {
  const mainScrollRef = React.useRef(null);
  const [showScrollHint, setShowScrollHint] = React.useState(true);

  React.useEffect(() => {
    const id = "sa-landing-scroll-hint-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = LANDING_SCROLL_HINT_CSS;
      document.head.appendChild(tag);
    }
  }, []);

  React.useEffect(() => {
    const id = "sa-landing-waves-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = LANDING_WAVES_CSS;
      document.head.appendChild(tag);
    }
  }, []);

  const scrollToWorkspace = () => {
    const el = document.getElementById("sa-workspace-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="sa-main-wrapper main-layout-chat"
      ref={mainScrollRef}
      style={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        overflowY: "auto",
        overflowX: "hidden",
      }}
      onScroll={(e) => {
        const top = e.currentTarget.scrollTop || 0;
        setShowScrollHint(top < 40);
      }}
    >
      <div
        className="particle-container sa-landing-hero"
        style={{
          height: "104vh",
          position: "relative",
        }}
      >
        <div className="particles">
          <span style={{ backgroundColor: "rgb(83 67 141)" }} />
          <span /><span style={{ backgroundColor: "rgb(83 67 141)" }} /><span />
          <span /><span style={{ backgroundColor: "rgb(83 67 141)" }} /><span />
          <span style={{ backgroundColor: "rgb(83 67 141)" }} /><span />
          <span style={{ backgroundColor: "rgb(83 67 141)" }} /><span /><span />
        </div>

        <div
          className="sa-landing-wave-svg"
          aria-hidden="true"
          style={{ "--sa-wave-svg": `url(${waveSvg})` }}
        />

        <div className="chat-main-content" style={{ minHeight: "calc(100vh - 60px)", position: "relative" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.2) 1px, transparent 0)", backgroundSize: "40px 40px", opacity: 0.4 }} />
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)", pointerEvents: "none" }} />
          <LandingChatView />
        </div>

        {showScrollHint ? (
          <button
            type="button"
            className="sa-scroll-hint"
            onClick={scrollToWorkspace}
            aria-label="Scroll to workspace section"
          >
            <span className="sa-scroll-hint__label">Workspace</span>
            <span className="sa-scroll-hint__chevrons" aria-hidden="true">
              <span className="sa-scroll-hint__chev" />
              <span className="sa-scroll-hint__chev" />
            </span>
          </button>
        ) : null}
      </div>

      <div id="sa-workspace-section" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <WorkspaceSection />
      </div>

      <div
        style={{
          backgroundColor: "#ffffff",
          borderTop: "0.5px solid rgba(0,0,0,0.08)",
          padding: "10px 24px",
          textAlign: "center",
        }}
      >
        <span style={{ color: "#94a3b8", fontSize: 12, fontWeight: 400, fontFamily: "'DM Sans', sans-serif" }}>
          © {getYear()} SuperAgentX &nbsp;·&nbsp; Version {APP_VERSION}
        </span>
      </div>
    </div>
  );
};

export default Landing;